import {
  Directive,
  ElementRef,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import intlTelInput from 'intl-tel-input';
import { Subscription } from 'rxjs';
import {
  AsYouType,
  getExampleNumber,
  CountryCode,
} from 'libphonenumber-js/max';
import examples from 'libphonenumber-js/examples.mobile.json';
import { TranslateService } from '@ngx-translate/core';
import { ar as arabicI18n } from 'intl-tel-input/i18n';
@Directive({
  selector: '[appIntlTelInput]',
  standalone: true,
})
export class IntlTelInputDirective implements OnInit, OnDestroy {
  private iti: any;
  @Input() defaultCountry = 'us';
  @Input() preferredCountries: string[] = ['us', 'gb', 'ca', 'au'];
  @Input() separateDialCode = true;
  @Output() countryChange = new EventEmitter<any>();
  @Output() validityChange = new EventEmitter<boolean>();
  @Output() phoneNumberChange = new EventEmitter<any>();
  @Output() phoneValidationError = new EventEmitter<string>();
  @Input() countryCode: string = '';
  private initialized = false;
  private langChangeSub?: Subscription;

  countryPhoneSpecs: {
    [key: string]: { min?: number; max?: number; exact?: number };
  } = {
    US: { exact: 10 }, // US: exactly 10 digits
    CA: { exact: 10 }, // Canada: exactly 10 digits
    GB: { min: 10, max: 11 }, // UK: 10 or 11 digits
    EG: { min: 10, max: 11 }, // Egypt: 10 or 11 digits
    AU: { exact: 9 }, // Australia: 9 digits
    IN: { exact: 10 }, // India: 10 digits
    DE: { min: 10, max: 11 }, // Germany: 10 or 11 digits
    FR: { exact: 9 }, // France: 9 digits
    IT: { exact: 10 }, // Italy: 10 digits
    ES: { min: 9, max: 9 }, // Spain: 9 digits
    JP: { exact: 10 }, // Japan: 10 digits
    CN: { min: 11, max: 11 }, // China: 11 digits
    BR: { min: 10, max: 11 }, // Brazil: 10 or 11 digits
    RU: { exact: 10 }, // Russia: 10 digits
    MX: { exact: 10 }, // Mexico: 10 digits
    // Add more countries as needed
  };
  constructor(
    private el: ElementRef,
    private control: NgControl,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const input = this.el.nativeElement;

    // Get current language for i18n
    const currentLang = this.translate.currentLang || this.translate.defaultLang || 'en';

    // Initialize with minimal options that don't rely on utils
    this.initializeIntlTelInput(input, currentLang);
    this.initialized = true;
    setTimeout(() => {
      this.preselectCountryByDialCode();
      this.updatePlaceholder(this.iti.getSelectedCountryData().iso2);
    }, 0);

    // Attach event listeners
    this.attachEventListeners(input);

    // Initial formatting
    setTimeout(() => {
      if (this.control.value) {
        input.value = this.control.value;
        this.updateValueManually();
      }
    }, 0);

    // Subscribe to language changes to reinitialize with new i18n
    this.langChangeSub = this.translate.onLangChange.subscribe((event) => {
      this.reinitializeWithLanguage(event.lang);
    });
  }

  private initializeIntlTelInput(input: HTMLInputElement, lang: string): void {
    this.iti = intlTelInput(input, {
      initialCountry: this.defaultCountry,
      separateDialCode: this.separateDialCode,
      formatOnDisplay: false,
      autoPlaceholder: 'off',
      nationalMode: false,
      ...(lang === 'ar' && { i18n: arabicI18n }),
    } as any);
  }

  private attachEventListeners(input: HTMLInputElement): void {
    // Listen for country changes
    input.addEventListener('countrychange', () => {
      const countryData = this.iti.getSelectedCountryData();
      this.countryChange.emit(countryData);

      // Get current input value
      const currentValue = input.value.trim();

      // If there's a value, clear it when country changes
      if (currentValue) {
        // Clear the input
        input.value = '';

        // Update the control value
        this.control.control?.setValue('');

        // Also emit the change
        this.phoneNumberChange.emit({
          fullNumber: '',
          nationalNumber: '',
          dialCode: countryData.dialCode,
          countryData: countryData,
        });
      }
      this.updatePlaceholder(countryData.iso2);

      this.updateValueManually();
    });

    // Update on blur
    input.addEventListener('blur', () => {
      this.updateValueManually();
    });

    // Update on input
    input.addEventListener('input', () => {
      this.updateValueManually();
    });
  }

  private reinitializeWithLanguage(lang: string): void {
    if (!this.iti) return;

    const input = this.el.nativeElement;

    // Save current state
    const currentCountryData = this.iti.getSelectedCountryData();
    const currentValue = input.value;

    // Destroy current instance
    this.iti.destroy();

    // Reinitialize with new language
    this.initializeIntlTelInput(input, lang);

    // Restore country selection
    if (currentCountryData?.iso2) {
      this.iti.setCountry(currentCountryData.iso2);
    }

    // Restore value
    if (currentValue) {
      input.value = currentValue;
    }

    // Update placeholder for the selected country
    this.updatePlaceholder(this.iti.getSelectedCountryData().iso2);
  }
  private preselectCountryByDialCode(): void {
    if (!this.countryCode || !this.iti) return;

    try {
      // Clean country code input (remove '+' if present)
      const dialCode = this.countryCode.replace(/^\+/, '');

      if (dialCode) {
        // Get current selected country data
        const initialData = this.iti.getSelectedCountryData();

        // If already set correctly, do nothing
        if (initialData && initialData.dialCode === dialCode) {
          return;
        }

        // Try to automatically select the country by iterating through available countries
        const input = this.el.nativeElement;
        let found = false;

        // Store initial value
        const initialValue = input.value;

        // First try direct selection
        try {
          // Set a sample number with the dialCode to trigger country detection
          input.value = `+${dialCode}`;
          // This triggers the country selection in some versions
          this.iti.handleUtils();

          // Check if it worked
          const data = this.iti.getSelectedCountryData();
          if (data && data.dialCode === dialCode) {
            found = true;
            this.countryChange.emit(data);
          }
        } catch (e) {
          console.log('Direct selection failed:', e);
        }

        // Reset input value
        input.value = initialValue;

        if (!found) {
          // If we get here, we couldn't find a matching country
          console.log('Failed to find country with dial code:', dialCode);
        }
      }
    } catch (error) {
      console.error('Error presetting country:', error);
    }
  }

  private updatePlaceholder(countryCode: string): void {
    const input = this.el.nativeElement;

    try {
      // Convert to uppercase and cast to CountryCode type
      const validCountryCode = countryCode.toUpperCase() as CountryCode;

      // Get example number using libphonenumber-js
      const exampleNumber = getExampleNumber(validCountryCode, examples);

      if (exampleNumber) {
        // Format as national number
        const formatter = new AsYouType(validCountryCode);
        const placeholder = formatter.input(exampleNumber.nationalNumber);
        input.placeholder = placeholder;
      } else {
        input.placeholder = 'Phone Number';
      }
    } catch (e) {
      input.placeholder = 'Phone Number';
    }
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
    if (this.iti) {
      this.iti.destroy();
    }
  }

  // Update form values without relying on utils
  private updateValueManually(): void {
    try {
      const input = this.el.nativeElement;
      const value = input.value.trim();
      const countryData = this.iti.getSelectedCountryData();

      // Get country code
      const dialCode = `+${countryData?.dialCode}` || '';

      // Get user input without the dial code prefix
      let nationalNumber = value;
      if (
        this.separateDialCode &&
        dialCode &&
        value.startsWith('+' + dialCode)
      ) {
        nationalNumber = value.substring(dialCode.length + 1).trim();
      }

      // Update form control with current value
      this.control.control?.setValue(value);

      // For parent form if it has mobile_country_code field
      const parentForm = this.control.control?.parent;
      if (parentForm && parentForm.get('mobile_country_code')) {
        parentForm.get('mobile_country_code')?.setValue(dialCode);
      }

      // Basic validation
      const isValid = this.validatePhoneNumber(value, dialCode);
      let errorMessage = '';
      this.validityChange.emit(isValid);
      this.phoneNumberChange.emit({
        fullNumber: value,
        nationalNumber: nationalNumber,
        dialCode: dialCode,
        countryData: countryData,
        isValidNumber: isValid,
      });

      if (!isValid) {
        // Provide specific error message based on validation issue
        if (!value) {
          errorMessage = this.translate.instant('buyer_register.primary_phone_required');
        } else if (value.length < 5) {
          errorMessage = this.translate.instant('buyer_register.phone_number_is_too_short');
        } else {
          errorMessage = `${this.translate.instant('buyer_register.invalid_phone_number_format_for')} ${countryData.name}`;
        }

        this.phoneValidationError.emit(errorMessage);
        this.validityChange.emit(false);
        this.control.control?.setErrors({ invalidPhone: true });
      } else {
        this.phoneValidationError.emit('');
        this.validityChange.emit(true);
      }
    } catch (error) {
      console.error('Error updating phone input:', error);
      this.validityChange.emit(false);
    }
  }

  private validatePhoneNumber(phoneNumber: string, dialCode: string): boolean {
    if (!phoneNumber || !dialCode) return false;

    // Strip non-digit characters
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    const countryData = this.iti.getSelectedCountryData();

    // Try to infer expected length from the placeholder examples in the UI
    // The placeholder contains a typical example number for the country
    const placeholder = this.el.nativeElement.placeholder || '';
    const placeholderDigits = placeholder.replace(/\D/g, '').length;

    // If we have a placeholder with digits, use that as the expected length
    // Otherwise fall back to the predefined specs
    if (placeholderDigits > 0) {
      // Allow a small variation in length (±1 digit) to account for different regional formats
      const minLength = Math.max(placeholderDigits - 1, 7);
      const maxLength = placeholderDigits + 1;
      return digitsOnly.length >= minLength && digitsOnly.length <= maxLength;
    }

    // If we couldn't infer from placeholder, use the static table as before
    const countryCode = countryData.iso2.toUpperCase();

    const specs = this.countryPhoneSpecs[countryCode] || { min: 8, max: 15 };

    if (specs.exact !== undefined) {
      return digitsOnly.length === specs.exact;
    } else {
      return (
        digitsOnly.length >= (specs?.min ?? 8) &&
        digitsOnly.length <= (specs?.max ?? 15)
      );
    }
  }
}
